import residentService from './residentService';
import apiClient from './apiClient';
import { formatToBackendDateTime } from '../utils/dateUtils';
import {
    AdmissionStatus,
    CareDashboardStats,
    CareDocumentDTO,
    CareDocumentType,
    CarePriority,
    CareRequestDTO,
    CareSLAReportDTO,
    CareStaffAssignmentDTO,
    CareStatus,
    ProspectiveResidentDTO,
    ResidentDTO,
    ShiftHandoverDTO,
} from '../types/crm.types';
import { PageResponse } from '../types/resident.types';

const SHIFT_HANDOVER_BASE = 'v1/shift-handover';
const CARE_REQUEST_BASE = 'v1/care-requests';

const now = Date.now();
const hoursFromNow = (hours: number) => new Date(now + hours * 60 * 60 * 1000).toISOString();
const daysAgo = (days: number) => new Date(now - days * 24 * 60 * 60 * 1000).toISOString();

let leads: ProspectiveResidentDTO[] = [
    {
        id: 'lead-1001',
        name: 'Amelia Brooks',
        responsibleParty: 'James Brooks',
        email: 'james.brooks@example.com',
        phone: '(555) 301-2201',
        source: 'Hospital Discharge',
        status: AdmissionStatus.INQUIRY,
        notes: 'Needs mobility support and medication supervision.',
        readinessScore: 62,
        createdAt: daysAgo(2),
    },
    {
        id: 'lead-1002',
        name: 'Walter Greene',
        responsibleParty: 'Lila Greene',
        email: 'lila.greene@example.com',
        phone: '(555) 301-2202',
        source: 'Referral',
        status: AdmissionStatus.TOURING,
        notes: 'Family touring facility later this week.',
        readinessScore: 78,
        createdAt: daysAgo(4),
    },
    {
        id: 'lead-1003',
        name: 'Margaret Cole',
        responsibleParty: 'Denise Cole',
        email: 'denise.cole@example.com',
        phone: '(555) 301-2203',
        source: 'Website',
        status: AdmissionStatus.WAITLIST,
        notes: 'Awaiting bed availability in memory care wing.',
        readinessScore: 85,
        createdAt: daysAgo(6),
    },
    {
        id: 'lead-1004',
        name: 'Samuel Bishop',
        responsibleParty: 'Rina Bishop',
        email: 'rina.bishop@example.com',
        phone: '(555) 301-2204',
        source: 'Walk-in',
        status: AdmissionStatus.ADMITTED,
        notes: 'Move-in completed successfully.',
        readinessScore: 96,
        createdAt: daysAgo(1),
    },
];

let tickets: CareRequestDTO[] = [
    {
        id: 'cr-1001',
        requestNumber: 'CR-1001',
        residentId: 'res-001',
        residentName: 'Eleanor Vance',
        unitId: '102A',
        description: 'Resident needs respiratory reassessment after morning wheezing episode.',
        priority: CarePriority.URGENT,
        category: 'Medical',
        subcategory: 'Respiratory',
        status: CareStatus.NEW,
        slaDeadline: hoursFromNow(4),
        isEscalated: true,
        createdAt: daysAgo(0),
        updatedAt: daysAgo(0),
    },
    {
        id: 'cr-1002',
        requestNumber: 'CR-1002',
        residentId: 'res-002',
        residentName: 'Arthur Pendelton',
        unitId: '204',
        description: 'Follow up on medication refusal and provide behavior support plan.',
        priority: CarePriority.HIGH,
        category: 'Clinical',
        subcategory: 'Medication',
        status: CareStatus.ASSIGNED,
        assignedTo: 'Sarah Johnson',
        slaDeadline: hoursFromNow(8),
        isEscalated: false,
        createdAt: daysAgo(1),
        updatedAt: daysAgo(0),
    },
    {
        id: 'cr-1003',
        requestNumber: 'CR-1003',
        residentId: 'res-003',
        residentName: 'Martha Stewart',
        unitId: '110B',
        description: 'Replace call bell battery and inspect bedside rail.',
        priority: CarePriority.MEDIUM,
        category: 'Facility',
        subcategory: 'Maintenance',
        status: CareStatus.IN_PROGRESS,
        assignedTo: 'Mike Turner',
        slaDeadline: hoursFromNow(16),
        isEscalated: false,
        createdAt: daysAgo(2),
        updatedAt: daysAgo(1),
    },
    {
        id: 'cr-1004',
        requestNumber: 'CR-1004',
        residentId: 'res-004',
        residentName: 'Grace Miller',
        unitId: '118C',
        description: 'Review meal preference update and dietician note.',
        priority: CarePriority.LOW,
        category: 'Dietary',
        subcategory: 'Meal Preference',
        status: CareStatus.RESOLVED,
        assignedTo: 'Tina Brooks',
        slaDeadline: hoursFromNow(24),
        isEscalated: false,
        createdAt: daysAgo(3),
        updatedAt: daysAgo(1),
    },
];

const documents: CareDocumentDTO[] = [
    {
        id: 'doc-1001',
        title: 'Resident Consent Form - Amelia Brooks',
        type: CareDocumentType.CONSENT_FORM,
        fileSize: '248 KB',
        createdAt: daysAgo(3),
        uploadedBy: 'Admissions Team',
    },
    {
        id: 'doc-1002',
        title: 'Insurance Verification - Walter Greene',
        type: CareDocumentType.INSURANCE,
        fileSize: '312 KB',
        createdAt: daysAgo(6),
        uploadedBy: 'Front Desk',
    },
    {
        id: 'doc-1003',
        title: 'Medical Summary - Margaret Cole',
        type: CareDocumentType.MEDICAL_RECORD,
        fileSize: '1.4 MB',
        createdAt: daysAgo(1),
        uploadedBy: 'Clinical Team',
    },
    {
        id: 'doc-1004',
        title: 'Admission Agreement - Samuel Bishop',
        type: CareDocumentType.ADMISSION_AGREEMENT,
        fileSize: '520 KB',
        createdAt: daysAgo(2),
        uploadedBy: 'Admissions Team',
    },
];

const reports: CareSLAReportDTO[] = [
    { month: 'Jan', targetMet: 92, targetBreached: 8, avgResolutionTime: 4.5 },
    { month: 'Feb', targetMet: 94, targetBreached: 6, avgResolutionTime: 4.1 },
    { month: 'Mar', targetMet: 91, targetBreached: 9, avgResolutionTime: 4.8 },
    { month: 'Apr', targetMet: 96, targetBreached: 4, avgResolutionTime: 3.9 },
    { month: 'May', targetMet: 95, targetBreached: 5, avgResolutionTime: 4.0 },
    { month: 'Jun', targetMet: 97, targetBreached: 3, avgResolutionTime: 3.7 },
];

const assignments: CareStaffAssignmentDTO[] = [
    {
        id: 'asg-1001',
        residentName: 'Eleanor Vance',
        unitLabel: 'Room 102A',
        staffName: 'Sarah Johnson',
        taskDescription: 'Respiratory check and nebulizer follow-up before noon rounds.',
        scheduledAt: hoursFromNow(2),
        status: 'SCHEDULED',
    },
    {
        id: 'asg-1002',
        residentName: 'Arthur Pendelton',
        unitLabel: 'Room 204',
        staffName: 'Mark Lewis',
        taskDescription: 'Behavior monitoring and medication re-approach after lunch.',
        scheduledAt: hoursFromNow(5),
        status: 'ON_CALL',
    },
];

// Temporarily store handovers in memory for fallback/demo
let handovers: ShiftHandoverDTO[] = [
    {
        id: 'ho-1001',
        handedOverAt: new Date(new Date().getTime() - 1000 * 60 * 30).toISOString(),
        residentId: 'res-001',
        residentName: 'Eleanor Vance',
        roomNumber: '102A',
        situation: 'Eleanor experienced mild shortness of breath earlier, which resolved after 15 mins.',
        background: 'History of asthma and mild hypertension. On regular nebulizer.',
        assessment: 'Lungs clear now. O2 sat is 96% on room air. BP slightly elevated.',
        recommendation: 'Monitor respiratory rate every 4 hours. Keep inhaler nearby.',
        safetyAlerts: ['Fall Risk', 'Asthma'],
        isCritical: false,
        mood: false,
        vitals: { bp: '138/85', temp: '98.6', pulse: '88', spO2: '96' },
        checklist: [
            { task: 'Meds Administered', completed: true },
            { task: 'Chart Updated', completed: true },
            { task: 'ADLs Completed', completed: true },
            { task: 'Family Notified', completed: false },
        ],
        taskCategories: [
            {
                key: 'vitals', label: 'Vital Signs', color: '#0077B6',
                tasks: [
                    { id: 'v-weight', label: 'Weight', completed: false },
                    { id: 'v-bp', label: 'Blood Pressure', completed: true },
                    { id: 'v-temp', label: 'Temperature', completed: true },
                    { id: 'v-pulse', label: 'Pulse', completed: true },
                ],
            },
        ],
    },
    {
        id: 'ho-1002',
        handedOverAt: new Date(new Date().getTime() - 1000 * 60 * 120).toISOString(),
        residentId: 'res-002',
        residentName: 'Arthur Pendelton',
        roomNumber: '204',
        situation: 'Arthur refused his morning medications and was quite agitated.',
        background: 'Dementia, frequently confused in the mornings. Type 2 Diabetic.',
        assessment: 'Combative behavior observed. Blood sugar was 145 mg/dL. Skin is intact.',
        recommendation: 'Try approaching him later with a cup of tea for meds. Re-check blood sugar.',
        safetyAlerts: ['Wandering Risk'],
        isCritical: true,
        mood: true,
        vitals: { bp: '142/90', temp: '98.2', pulse: '92', spO2: '98' },
        checklist: [
            { task: 'Meds Administered', completed: false },
            { task: 'Chart Updated', completed: true },
            { task: 'ADLs Completed', completed: false },
            { task: 'Family Notified', completed: true },
        ],
        taskCategories: [
            {
                key: 'meals', label: 'Meals', color: '#d97706',
                tasks: [{ id: 'm-br', label: 'Breakfast', completed: false }],
            },
        ],
    },
    {
        id: 'ho-1003',
        handedOverAt: new Date(new Date().getTime() - 1000 * 60 * 240).toISOString(),
        residentId: 'res-003',
        residentName: 'Martha Stewart',
        roomNumber: '110B',
        situation: 'Routine shift, slept well through the morning, requested an extra blanket.',
        background: 'Osteoarthritis, uses walker. Generally independent.',
        assessment: 'Comfortable. Mobility is stable. Vitals are within normal limits.',
        recommendation: 'None. Continue typical care routine.',
        safetyAlerts: [],
        isCritical: false,
        mood: false,
        vitals: { bp: '120/80', temp: '98.0', pulse: '72', spO2: '99' },
        checklist: [
            { task: 'Meds Administered', completed: true },
            { task: 'Chart Updated', completed: true },
            { task: 'ADLs Completed', completed: true },
            { task: 'Family Notified', completed: true },
        ],
        taskCategories: [],
    },
];

const buildDashboardStats = (): CareDashboardStats => ({
    totalActiveRequests: tickets.filter((ticket) => ![CareStatus.RESOLVED, CareStatus.CLOSED].includes(ticket.status)).length,
    pendingRequests: tickets.filter((ticket) => [CareStatus.NEW, CareStatus.ASSIGNED].includes(ticket.status)).length,
    criticalCareAlerts: tickets.filter((ticket) => ticket.priority === CarePriority.URGENT || ticket.isEscalated).length,
    admissionsInPipeline: leads.filter((lead) => ![AdmissionStatus.ADMITTED, AdmissionStatus.LOST].includes(lead.status)).length,
    staffWorkload: {
        'Sarah Johnson': 4,
        'Mark Lewis': 3,
        'Tina Brooks': 2,
    },
});

const careService = {
    getCustomers: async (facilityId?: string | null): Promise<ResidentDTO[]> => {
        try {
            const fid = facilityId || localStorage.getItem('currentTenantId') || undefined;
            if (fid) {
                const res = await residentService.getAllResidents(0, 10000, fid, 'ACTIVE');
                return res.content || [];
            }
            const res = await residentService.getAllResidents(0, 10000, fid);
            return res.content || [];
        } catch (error) {
            console.error('Failed to get residents:', error);
            const res = await residentService.getAllResidents(0, 10000);
            return res.content || [];
        }
    },

    getDashboardStats: async (): Promise<CareDashboardStats> => buildDashboardStats(),

    getTickets: async (residents?: ResidentDTO[], facilityId?: string, page = 0, size = 15): Promise<CareRequestDTO[]> => {
        try {
            const url = facilityId ? `${CARE_REQUEST_BASE}/facility/${facilityId}` : CARE_REQUEST_BASE;
            const response = await apiClient.get<any>(url, {
                params: { page, size }
            });
            const data = response.data;
            let items: any[] = [];
            if (Array.isArray(data)) {
                items = data;
            } else if (data && Array.isArray(data.content)) {
                items = data.content;
            }
            return items.map(item => {
                const residentId = item.residentId || '';
                const resident = residents?.find(r => r.id === residentId);
                const residentName = item.residentName || (resident ? `${resident.firstName} ${resident.lastName}` : 'Resident');
                return {
                    id: item.id || item.requestId || item.careRequestId || '',
                    requestNumber: item.requestId || item.requestNumber || `CR-${String(item.careRequestId || item.id).substring(0, 4)}`,
                    residentId: residentId,
                    residentName: residentName,
                    unitId: item.unitId || resident?.roomNumber || 'Unassigned',
                    description: item.description || '',
                    priority: (item.priority || 'MEDIUM').toUpperCase() as CarePriority,
                    category: item.category || 'General',
                    subcategory: item.residentNeed || item.subcategory || '',
                    status: (item.status || 'NEW').toUpperCase() as CareStatus,
                    assignedTo: item.assignedTo,
                    assignedToName: item.assignedToName || '',
                    closedRemark: item.closedRemark || item.closeRemark || item.remark || item.resolutionNote || '',
                    resolutionNote: item.resolutionNote || item.closedRemark || item.closeRemark || item.remark || '',
                    slaDeadline: item.responseDeadline || item.slaDeadline || new Date().toISOString(),
                    isEscalated: !!item.isEscalated,
                    createdAt: item.createdAt,
                    updatedAt: item.updatedAt,
                };
            });
        } catch (error) {
            console.warn('Failed to fetch tickets from API, using fallback', error);
            return [...tickets];
        }
    },

    getPaginatedTickets: async (residents?: ResidentDTO[], facilityId?: string, page = 0, size = 15) => {
        try {
            const url = facilityId ? `${CARE_REQUEST_BASE}/facility/${facilityId}` : CARE_REQUEST_BASE;
            const response = await apiClient.get<any>(url, { params: { page, size } });
            const data = response.data;
            let items: any[] = [];
            let totalPages = 1;
            let totalElements = 0;

            if (Array.isArray(data)) {
                totalElements = data.length;
                totalPages = Math.ceil(totalElements / size) || 1;
                // Since backend sent unpaginated array, manually paginate
                items = data.slice(page * size, (page + 1) * size);
            } else if (data && Array.isArray(data.content)) {
                items = data.content;
                totalPages = data.totalPages || 1;
                totalElements = data.totalElements || data.content.length;
            }

            const content = items.map(item => {
                const residentId = item.residentId || '';
                const resident = residents?.find(r => r.id === residentId);
                const residentName = item.residentName || (resident ? `${resident.firstName} ${resident.lastName}` : 'Resident');
                return {
                    id: item.id || item.requestId || item.careRequestId || '',
                    requestNumber: item.requestId || item.requestNumber || `CR-${String(item.careRequestId || item.id).substring(0, 4)}`,
                    residentId: residentId,
                    residentName: residentName,
                    unitId: item.unitId || resident?.roomNumber || 'Unassigned',
                    description: item.description || '',
                    priority: (item.priority || 'MEDIUM').toUpperCase() as CarePriority,
                    category: item.category || 'General',
                    subcategory: item.residentNeed || item.subcategory || '',
                    status: (item.status || 'NEW').toUpperCase() as CareStatus,
                    assignedTo: item.assignedTo,
                    assignedToName: item.assignedToName || '',
                    closedRemark: item.closedRemark || item.closeRemark || item.remark || item.resolutionNote || '',
                    resolutionNote: item.resolutionNote || item.closedRemark || item.closeRemark || item.remark || '',
                    slaDeadline: item.responseDeadline || item.slaDeadline || new Date().toISOString(),
                    isEscalated: !!item.isEscalated,
                    createdAt: item.createdAt,
                    updatedAt: item.updatedAt,
                } as CareRequestDTO;
            });
            return { content, totalPages, totalElements };
        } catch (error) {
            console.warn('Failed to fetch paginated tickets from API, using fallback', error);
            const totalElements = tickets.length;
            const totalPages = Math.ceil(totalElements / size) || 1;
            const content = tickets.slice(page * size, (page + 1) * size);
            return { content, totalPages, totalElements };
        }
    },

    searchTickets: async (facilityId: string, globalSearch: string, residents?: ResidentDTO[], signal?: AbortSignal): Promise<CareRequestDTO[]> => {
        try {
            const response = await apiClient.get<any[]>(`${CARE_REQUEST_BASE}/search`, {
                params: {
                    facilityId,
                    globalSearch
                },
                signal
            });
            const data = response.data;
            let items: any[] = [];
            if (Array.isArray(data)) {
                items = data;
            } else if (data && (data as any).content && Array.isArray((data as any).content)) {
                items = (data as any).content;
            }
            return items.map(item => {
                const residentId = item.residentId || '';
                const resident = residents?.find(r => r.id === residentId);
                const residentName = item.residentName || (resident ? `${resident.firstName} ${resident.lastName}` : 'Resident');

                return {
                    id: item.id || item.requestId || item.careRequestId || '',
                    requestNumber: item.requestId || item.requestNumber || `CR-${String(item.careRequestId || item.id).substring(0, 4)}`,
                    residentId: residentId,
                    residentName: residentName,
                    unitId: item.unitId || resident?.roomNumber || 'Unassigned',
                    description: item.description || '',
                    priority: (item.priority || 'MEDIUM').toUpperCase() as CarePriority,
                    category: item.category || 'General',
                    subcategory: item.residentNeed || item.subcategory || '',
                    status: (item.status || 'NEW').toUpperCase() as CareStatus,
                    assignedTo: item.assignedTo,
                    assignedToName: item.assignedToName || '',
                    slaDeadline: item.responseDeadline || item.slaDeadline || new Date().toISOString(),
                    isEscalated: !!item.isEscalated,
                    createdAt: item.createdAt,
                    updatedAt: item.updatedAt,
                };
            });
        } catch (error) {
            console.error('Failed to search tickets:', error);
            return [];
        }
    },

    getTicketById: async (id: string, residents?: ResidentDTO[]): Promise<CareRequestDTO> => {
        try {
            const response = await apiClient.get<any>(`${CARE_REQUEST_BASE}/${id}`);
            const item = response.data;
            const residentId = item.residentId || '';
            const resident = residents?.find(r => r.id === residentId);
            const residentName = item.residentName || (resident ? `${resident.firstName} ${resident.lastName}` : 'Resident');

            return {
                id: item.id || item.requestId || item.careRequestId || id,
                requestNumber: item.requestId || item.requestNumber || `CR-${String(item.careRequestId || item.id).substring(0, 4)}`,
                residentId: residentId,
                residentName: residentName,
                unitId: item.unitId || resident?.roomNumber || 'Unassigned',
                description: item.description || '',
                priority: (item.priority || 'MEDIUM').toUpperCase() as CarePriority,
                category: item.category || 'General',
                subcategory: item.residentNeed || item.subcategory || '',
                status: (item.status || 'NEW').toUpperCase() as CareStatus,
                assignedTo: item.assignedTo,
                assignedToName: item.assignedToName || '',
                closedRemark: item.closedRemark || item.closeRemark || item.remark || item.resolutionNote || '',
                resolutionNote: item.resolutionNote || item.closedRemark || item.closeRemark || item.remark || '',
                slaDeadline: item.responseDeadline || item.slaDeadline || new Date().toISOString(),
                isEscalated: !!item.isEscalated,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
            };
        } catch (error) {
            console.error(`Failed to fetch ticket ${id}`, error);
            const fallback = tickets.find(t => t.id === id);
            if (fallback) return fallback;
            // Throw error but don't break the whole app
            throw error;
        }
    },

    createTicket: async (data: any): Promise<CareRequestDTO> => {
        // Use the ID provided in the curl command to avoid 404 "User not found" errors
        // specifically with 'ad9b2948-2782-4f0d-8fc2-4d7f8113ac0f'
        let requestedById = 'f1b2c3d4-5e6a-4f8b-9c7d-0a1e2b3c4d80';
        try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const parsedUser = JSON.parse(storedUser);
                if (parsedUser && parsedUser.id) {
                    requestedById = parsedUser.id;
                }
            }
        } catch (e) {
            console.warn('Failed to parse user from localStorage', e);
        }

        const payload = {
            residentId: data.residentId,
            requestedById: data.requestedById || data.requestedBy || requestedById,
            assignedTo: data.assignedTo || null,
            residentNeed: data.subcategory || data.residentNeed || 'Care needed',
            description: data.description || '',
            status: (data.status || 'PENDING').toUpperCase(),
            priority: (data.priority || 'MEDIUM').toUpperCase(),
            category: (data.category || 'GENERAL').toUpperCase(),
            responseDeadline: data.slaDeadline || data.responseDeadline || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        };

        const response = await apiClient.post<any>(CARE_REQUEST_BASE, payload);
        const item = response.data;

        // Map response back to DTO
        const created: CareRequestDTO = {
            id: item.id || item.requestId || item.careRequestId || `cr-${Date.now()}`,
            requestNumber: item.requestId || item.requestNumber || `CR-${Date.now().toString().slice(-4)}`,
            residentId: item.residentId || data.residentId,
            residentName: data.residentName || 'Resident',
            unitId: data.unitId || 'Unassigned',
            description: item.description || data.description,
            priority: (item.priority || data.priority || 'MEDIUM').toUpperCase() as CarePriority,
            category: item.category || data.category,
            subcategory: item.residentNeed || item.subcategory || data.subcategory,
            status: (item.status || data.status || 'NEW').toUpperCase() as CareStatus,
            assignedTo: item.assignedTo || data.assignedTo,
            closedRemark: item.closedRemark || item.closeRemark || item.remark || data.closedRemark || item.resolutionNote || '',
            resolutionNote: item.resolutionNote || item.closedRemark || item.closeRemark || item.remark || data.closedRemark || '',
            slaDeadline: item.responseDeadline || item.slaDeadline || data.slaDeadline,
            createdAt: item.createdAt || new Date().toISOString(),
            updatedAt: item.updatedAt || new Date().toISOString(),
        };

        tickets = [created, ...tickets];
        return created;
    },

    updateTicket: async (id: string, data: Partial<CareRequestDTO>): Promise<CareRequestDTO> => {
        let requestedById = 'f1b2c3d4-5e6a-4f8b-9c7d-0a1e2b3c4d80';
        try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const parsedUser = JSON.parse(storedUser);
                if (parsedUser && parsedUser.id) {
                    requestedById = parsedUser.id;
                }
            }
        } catch (e) {
            console.warn('Failed to parse user from localStorage', e);
        }

        const payload = {
            residentId: data.residentId,
            requestedById: data.requestedById || data.requestedBy || requestedById,
            assignedTo: data.assignedTo || null,
            residentNeed: data.subcategory,
            description: data.description,
            status: data.status,
            priority: data.priority,
            category: data.category?.toUpperCase(),
            responseDeadline: data.slaDeadline ? data.slaDeadline.split('.')[0] : null
        };

        const response = await apiClient.put(`${CARE_REQUEST_BASE}/${id}`, payload);
        const item = response.data;

        const updated: CareRequestDTO = {
            id: item.id || item.requestId || item.careRequestId || id,
            requestNumber: item.requestId || item.requestNumber || data.requestNumber || '',
            residentId: item.residentId || data.residentId || '',
            residentName: data.residentName || 'Resident',
            unitId: data.unitId || 'Unassigned',
            description: item.description || data.description || '',
            priority: (item.priority || data.priority || 'MEDIUM').toUpperCase() as CarePriority,
            category: item.category || data.category || '',
            subcategory: item.residentNeed || item.subcategory || data.subcategory || '',
            status: (item.status || data.status || 'NEW').toUpperCase() as CareStatus,
            assignedTo: item.assignedTo || data.assignedTo,
            closedRemark: item.closedRemark || item.closeRemark || item.remark || data.closedRemark || item.resolutionNote || '',
            resolutionNote: item.resolutionNote || item.closedRemark || item.closeRemark || item.remark || data.closedRemark || '',
            slaDeadline: item.responseDeadline || item.slaDeadline || data.slaDeadline || '',
            createdAt: item.createdAt || data.createdAt || new Date().toISOString(),
            updatedAt: item.updatedAt || new Date().toISOString(),
        };

        tickets = tickets.map(t => t.id === id ? updated : t);
        return updated;
    },

    updateTicketStatus: async (id: string, status: string, assignedTo?: string, closedRemark?: string): Promise<any> => {
        const payload: any = { status };
        if (assignedTo) payload.assignedTo = assignedTo;
        if (closedRemark) {
            payload.closedRemark = closedRemark;
            payload.resolutionNote = closedRemark;
        }
        const response = await apiClient.patch(`${CARE_REQUEST_BASE}/${id}/status`, payload);
        // update local fallback reference if needed
        const index = tickets.findIndex(t => t.id === id);
        if (index !== -1) {
            tickets[index].status = status as CareStatus;
            if (assignedTo) tickets[index].assignedTo = assignedTo;
            if (closedRemark) {
                tickets[index].closedRemark = closedRemark;
                tickets[index].resolutionNote = closedRemark;
            }
        }
        return response.data;
    },



    getLeads: async (): Promise<ProspectiveResidentDTO[]> => [...leads],

    createLead: async (data: Partial<ProspectiveResidentDTO>): Promise<ProspectiveResidentDTO> => {
        const created: ProspectiveResidentDTO = {
            id: `lead-${Date.now()}`,
            name: data.name || 'New Prospect',
            responsibleParty: data.responsibleParty || 'Family Contact',
            email: data.email,
            phone: data.phone,
            source: data.source || 'Website',
            status: data.status || AdmissionStatus.INQUIRY,
            notes: data.notes,
            readinessScore: data.readinessScore ?? 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };

        leads = [created, ...leads];
        return created;
    },

    updateLead: async (id: string, data: Partial<ProspectiveResidentDTO>): Promise<ProspectiveResidentDTO> => {
        const index = leads.findIndex((lead) => lead.id === id);
        if (index === -1) {
            throw new Error('Lead not found');
        }

        leads[index] = {
            ...leads[index],
            ...data,
            updatedAt: new Date().toISOString(),
        };

        return leads[index];
    },

    updateLeadStatus: async (id: string, newStatus: AdmissionStatus): Promise<ProspectiveResidentDTO> => {
        return careService.updateLead(id, { status: newStatus });
    },

    getDocuments: async (): Promise<CareDocumentDTO[]> => [...documents],

    getSLAReports: async (): Promise<CareSLAReportDTO[]> => [...reports],

    getAssignments: async (): Promise<CareStaffAssignmentDTO[]> => [...assignments],

    mapToShiftHandover: (item: any): ShiftHandoverDTO => ({
        id: item.id || item.shiftHandoverId || '',
        residentId: item.residentId || '',
        residentName: item.residentName || '',
        roomNumber: item.roomNumber || '',
        situation: item.situation || '',
        background: item.background || '',
        assessment: item.assessment || '',
        recommendation: item.recommendation || '',
        safetyAlerts: item.safetyAlerts || [],
        isCritical: !!item.criticalAlert,
        status: item.status || '',
        summary: item.summary || '',
        mood: !!item.mood,
        vitals: {
            bp: item.dailyBloodPressure || '',
            temp: item.temperature || '',
            pulse: item.pulseRate || '',
            spO2: item.spO2 || '',
        },
        checklist: [
            { task: 'Meds Administered', completed: !!item.medsAdministered },
            { task: 'Chart Updated', completed: !!item.chartUpdated },
            { task: 'ADLs Completed', completed: !!item.adlsCompleted },
            {
                task: 'Get Vitals', completed: !!(item.bpRecorded || item.temperature
                    || item.pulseRecorded)
            },
        ],
        taskCategories: [
            {
                key: 'bowel', label: 'Bowel', color: '#0891b2',
                tasks: [
                    { id: 'bw-check', label: 'Bowel Movement', completed: !!item.bowel },
                    { id: 'ur-check', label: 'Urinary', completed: !!item.urinary },
                    { id: 'cd-check', label: 'Catheter/Drain', completed: !!item.catheterDrain },
                    { id: 'ic-check', label: 'Incontinence Care', completed: !!item.incontinenceCare },
                ]
            },
            {
                key: 'bodycare', label: 'Body Care', color: '#059669',
                tasks: [
                    { id: 'bc-bath', label: 'Full Bath', completed: !!item.fullBath },
                    { id: 'bc-oral', label: 'Oral Care', completed: !!item.oralCare },
                    { id: 'bc-dress', label: 'Assist with Clothing', completed: !!item.assistWithClothing },
                    { id: 'bc-shampoo', label: 'Shampoo/Soap', completed: !!item.shampooDash },
                    { id: 'bc-partial', label: 'Partial Bath', completed: !!item.partialBath },
                    { id: 'bc-shave', label: 'Shave', completed: !!item.shave },
                    { id: 'bc-hair', label: 'Comb Hair', completed: !!item.combHair },
                    { id: 'bc-hand-nail', label: 'Hand Nail Care', completed: !!item.handNailCare },
                    { id: 'bc-nail', label: 'Toe Nail Care', completed: !!item.toeNailCare },
                ],
            },
            {
                key: 'skincare', label: 'Skin Care', color: '#db2777',
                tasks: [
                    { id: 'sc-barrier', label: 'Apply Skin Barrier', completed: !!item.applySkinBarrier },
                    { id: 'sc-pedi', label: 'Pedicare', completed: !!item.pedicare },
                    { id: 'sc-lower', label: 'Lower Extremities', completed: !!item.lowerExtremities },
                    { id: 'sc-lotion', label: 'Apply Lotion', completed: !!item.applyLotion },
                    { id: 'sc-elevate', label: 'Elevate Leg', completed: !!item.elevateLeg },
                ]
            },
            {
                key: 'meals', label: 'Meals', color: '#d97706',
                tasks: [
                    { id: 'm-br', label: 'Breakfast', completed: !!item.breakfast },
                    { id: 'm-lu', label: 'Lunch', completed: !!item.lunch },
                    { id: 'm-di', label: 'Dinner', completed: !!item.dinner },
                    { id: 'o-snack', label: 'Snack/Water', completed: !!item.snackWater },
                ],
            },
            {
                key: 'bed', label: 'Bed', color: '#7c3aed',
                tasks: [
                    { id: 'bd-med', label: 'Medication', completed: !!item.medicationGiven },
                    { id: 'bd-mood', label: 'Mood', completed: !!item.mood },
                    { id: 'bd-check', label: 'Night Checkup', completed: !!item.nightCheckup },
                ],
            },
        ],
        handedOverAt: item.handedOverAt || item.createdAt || new Date().toISOString(),
        updatedAt: item.updatedAt || item.lastModifiedDate || item.modifiedAt || item.handedOverAt || item.createdAt || new Date().toISOString(),
        handoverNote: item.handoverNote || '',
        history: item.history || [],
        completionRatio: item.completionRatio || '0/0',
    }),

    getHandovers: async (
        facilityId?: string | null,
        page: number = 0,
        size: number = 10,
    ): Promise<ShiftHandoverDTO[]> => {
        try {
            if (facilityId) {
                const response = await apiClient.get<PageResponse<any>>(`${SHIFT_HANDOVER_BASE}/facility/${facilityId}`, {
                    params: { page, size },
                });
                const items = Array.isArray(response.data?.content) ? response.data.content : [];
                return items.map(careService.mapToShiftHandover);
            }

            const response = await apiClient.get<any>(SHIFT_HANDOVER_BASE);
            if (Array.isArray(response.data)) {
                return response.data.map(careService.mapToShiftHandover);
            }
            if (response.data?.content) {
                return response.data.content.map(careService.mapToShiftHandover);
            }
            return [];
        } catch (error) {
            console.warn('getHandovers API failed, using local fallback', error);
            return [...handovers].reverse();
        }
    },

    searchHandovers: async (facilityId: string, searchTerm: string, status?: string): Promise<ShiftHandoverDTO[]> => {
        try {
            const params: any = { facilityId };
            if (searchTerm) params.searchTerm = searchTerm;
            if (status && status !== 'ALL' && status !== '') params.status = status;

            const response = await apiClient.get<any>(`${SHIFT_HANDOVER_BASE}/search/all`, {
                params
            });
            const data = response.data;
            const items = Array.isArray(data) ? data : (data?.content || []);
            return items.map(careService.mapToShiftHandover);
        } catch (error) {
            console.error('Failed to search handovers:', error);
            return [];
        }
    },

    getHandoverByResidentId: async (residentId: string): Promise<ShiftHandoverDTO | null> => {
        try {
            const response = await apiClient.get<any>(`${SHIFT_HANDOVER_BASE}/resident/${residentId}`);
            if (!response.data) return null;
            const item = response.data;
            return careService.mapToShiftHandover(item);
        } catch (error) {
            console.warn(`Failed to fetch handover for resident ${residentId}`, error);
            return null;
        }
    },

    /**
     * Calls GET /api/v1/shift-handover/null — required by the backend as a
     * prerequisite before creating/updating a handover for the first time.
     * The response (if any) may contain a template/default record.
     */
    getHandoverByNullResidentId: async (): Promise<ShiftHandoverDTO | null> => {
        try {
            const response = await apiClient.get<any>(`${SHIFT_HANDOVER_BASE}/null`);
            if (!response.data) return null;
            return careService.mapToShiftHandover(response.data);
        } catch (error) {
            // This call is a prerequisite ping — failure is non-fatal
            console.warn('getHandoverByNullResidentId call failed (non-fatal):', error);
            return null;
        }
    },

    createHandover: async (data: Partial<ShiftHandoverDTO>): Promise<ShiftHandoverDTO> => {
        try {
            const hasVitals = !!data.vitals?.bp || !!data.vitals?.temp || !!data.vitals?.pulse;
            const payload = {
                residentId: data.residentId,
                situation: data.situation,
                background: data.background,
                assessment: data.assessment,
                recommendation: data.recommendation,
                criticalAlert: !!data.isCritical,
                status: data.status || '',
                updatedAt: formatToBackendDateTime(new Date()),
                medsAdministered: data.checklist?.find((t) => t.task === 'Meds Administered')?.completed || false,
                adlsCompleted: data.checklist?.find((t) => t.task === 'ADLs Completed')?.completed || false,
                chartUpdated: data.checklist?.find((t) => t.task === 'Chart Updated')?.completed || false,
                familyNotified: false,
                weightRecorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,
                bpRecorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,
                tempRecorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,
                pulseRecorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,
                respRecorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,
                spO2Recorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,
                sugarRecorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,

                // Vitals Strings
                dailyBloodPressure: data.vitals?.bp || '',
                dailyBloodSugar: (data as any).vitals?.sugar || '',
                dailyWeight: (data as any).vitals?.weight || '',
                temperature: data.vitals?.temp || '',
                pulseRate: data.vitals?.pulse || '',
                respirationRate: (data as any).vitals?.resp || '',
                spO2: data.vitals?.spO2 || '',
                oxygenLevel: data.vitals?.spO2 || '',
                painLevel: (data as any).vitals?.pain || '0/10',
                levelOfConsciousness: (data as any).vitals?.loc || 'Alert and Oriented',

                // Bowel & Urinary
                bowel: data.taskCategories?.find((c) => c.key === 'bowel')?.tasks.find((t) => t.id === 'bw-check')?.completed || false,
                urinary: data.taskCategories?.find((c) => c.key === 'bowel')?.tasks.find((t) => t.id === 'ur-check')?.completed || false,
                catheterDrain: data.taskCategories?.find((c) => c.key === 'bowel')?.tasks.find((t) => t.id === 'cd-check')?.completed || false,
                incontinenceCare: data.taskCategories?.find((c) => c.key === 'bowel')?.tasks.find((t) => t.id === 'ic-check')?.completed || false,
                bowelMovement: 'Normal',

                // Body Care
                fullBath: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-bath')?.completed || false,
                oralCare: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-oral')?.completed || false,
                assistWithClothing: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-dress')?.completed || false,
                shampooDash: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-shampoo')?.completed || false,
                partialBath: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-partial')?.completed || false,
                shave: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-shave')?.completed || false,
                combHair: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-hair')?.completed || false,
                handNailCare: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-hand-nail')?.completed || false,
                toeNailCare: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-nail')?.completed || false,

                // Skin Care
                applySkinBarrier: data.taskCategories?.find((c) => c.key === 'skincare')?.tasks.find((t) => t.id === 'sc-barrier')?.completed || false,
                pedicare: data.taskCategories?.find((c) => c.key === 'skincare')?.tasks.find((t) => t.id === 'sc-pedi')?.completed || false,
                lowerExtremities: data.taskCategories?.find((c) => c.key === 'skincare')?.tasks.find((t) => t.id === 'sc-lower')?.completed || false,
                applyLotion: data.taskCategories?.find((c) => c.key === 'skincare')?.tasks.find((t) => t.id === 'sc-lotion')?.completed || false,
                elevateLeg: data.taskCategories?.find((c) => c.key === 'skincare')?.tasks.find((t) => t.id === 'sc-elevate')?.completed || false,

                // Meals
                breakfast: data.taskCategories?.find((c) => c.key === 'meals')?.tasks.find((t) => t.id === 'm-br')?.completed || false,
                lunch: data.taskCategories?.find((c) => c.key === 'meals')?.tasks.find((t) => t.id === 'm-lu')?.completed || false,
                dinner: data.taskCategories?.find((c) => c.key === 'meals')?.tasks.find((t) => t.id === 'm-di')?.completed || false,
                mealPortion: (data as any).mealPortion || 'Full',
                bedMeal: data.taskCategories?.find((c) => c.key === 'meals')?.tasks.some(t => t.completed) || false,

                // Medication & Mood
                medicationGiven: data.taskCategories?.find((c) => c.key === 'bed')?.tasks.find((t) => t.id === 'bd-med')?.completed || false,
                mood: !!data.mood,

                // Night Checkup
                nightCheckup: data.taskCategories?.find((c) => c.key === 'bed')?.tasks.find((t) => t.id === 'bd-check')?.completed || false,

                // Other
                snackWater: data.taskCategories?.find((c) => c.key === 'meals')?.tasks.find((t) => t.id === 'o-snack')?.completed || false,

                handoverNote: data.handoverNote || '',
            };
            const response = await apiClient.post<any>(SHIFT_HANDOVER_BASE, payload);
            return response.data;
        } catch (error) {
            console.warn('createHandover API failed, using local fallback', error);
            const newHandover: ShiftHandoverDTO = {
                ...(data as ShiftHandoverDTO),
                id: Date.now().toString(),
                handedOverAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            handovers.push(newHandover);
            return newHandover;
        }
    },

    updateHandover: async (id: string, data: Partial<ShiftHandoverDTO>): Promise<ShiftHandoverDTO> => {
        try {
            const hasVitals = !!data.vitals?.bp || !!data.vitals?.temp || !!data.vitals?.pulse;
            const payload = {
                residentId: data.residentId,
                situation: data.situation,
                background: data.background,
                assessment: data.assessment,
                recommendation: data.recommendation,
                criticalAlert: !!data.isCritical,
                status: data.status || '',
                updatedAt: formatToBackendDateTime(new Date()),
                medsAdministered: data.checklist?.find((t) => t.task === 'Meds Administered')?.completed || false,
                adlsCompleted: data.checklist?.find((t) => t.task === 'ADLs Completed')?.completed || false,
                chartUpdated: data.checklist?.find((t) => t.task === 'Chart Updated')?.completed || false,
                familyNotified: false,
                weightRecorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,
                bpRecorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,
                tempRecorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,
                pulseRecorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,
                respRecorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,
                spO2Recorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,
                sugarRecorded: data.checklist?.find((t) => t.task === 'Get Vitals')?.completed || false,


                // Vitals Strings
                dailyBloodPressure: data.vitals?.bp || '',
                dailyBloodSugar: (data as any).vitals?.sugar || '',
                dailyWeight: (data as any).vitals?.weight || '',
                temperature: data.vitals?.temp || '',
                pulseRate: data.vitals?.pulse || '',
                respirationRate: (data as any).vitals?.resp || '',
                spO2: data.vitals?.spO2 || '',
                oxygenLevel: data.vitals?.spO2 || '',
                painLevel: (data as any).vitals?.pain || '0/10',
                levelOfConsciousness: (data as any).vitals?.loc || 'Alert and Oriented',

                // Bowel & Urinary
                bowel: data.taskCategories?.find((c) => c.key === 'bowel')?.tasks.find((t) => t.id === 'bw-check')?.completed || false,
                urinary: data.taskCategories?.find((c) => c.key === 'bowel')?.tasks.find((t) => t.id === 'ur-check')?.completed || false,
                catheterDrain: data.taskCategories?.find((c) => c.key === 'bowel')?.tasks.find((t) => t.id === 'cd-check')?.completed || false,
                incontinenceCare: data.taskCategories?.find((c) => c.key === 'bowel')?.tasks.find((t) => t.id === 'ic-check')?.completed || false,
                bowelMovement: 'Normal',

                // Body Care
                fullBath: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-bath')?.completed || false,
                oralCare: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-oral')?.completed || false,
                assistWithClothing: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-dress')?.completed || false,
                shampooDash: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-shampoo')?.completed || false,
                partialBath: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-partial')?.completed || false,
                shave: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-shave')?.completed || false,
                combHair: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-hair')?.completed || false,
                handNailCare: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-hand-nail')?.completed || false,
                toeNailCare: data.taskCategories?.find((c) => c.key === 'bodycare')?.tasks.find((t) => t.id === 'bc-nail')?.completed || false,

                // Skin Care
                applySkinBarrier: data.taskCategories?.find((c) => c.key === 'skincare')?.tasks.find((t) => t.id === 'sc-barrier')?.completed || false,
                pedicare: data.taskCategories?.find((c) => c.key === 'skincare')?.tasks.find((t) => t.id === 'sc-pedi')?.completed || false,
                lowerExtremities: data.taskCategories?.find((c) => c.key === 'skincare')?.tasks.find((t) => t.id === 'sc-lower')?.completed || false,
                applyLotion: data.taskCategories?.find((c) => c.key === 'skincare')?.tasks.find((t) => t.id === 'sc-lotion')?.completed || false,
                elevateLeg: data.taskCategories?.find((c) => c.key === 'skincare')?.tasks.find((t) => t.id === 'sc-elevate')?.completed || false,

                // Meals
                breakfast: data.taskCategories?.find((c) => c.key === 'meals')?.tasks.find((t) => t.id === 'm-br')?.completed || false,
                lunch: data.taskCategories?.find((c) => c.key === 'meals')?.tasks.find((t) => t.id === 'm-lu')?.completed || false,
                dinner: data.taskCategories?.find((c) => c.key === 'meals')?.tasks.find((t) => t.id === 'm-di')?.completed || false,
                mealPortion: (data as any).mealPortion || 'Full',
                bedMeal: data.taskCategories?.find((c) => c.key === 'meals')?.tasks.some(t => t.completed) || false,

                // Medication & Mood
                medicationGiven: data.taskCategories?.find((c) => c.key === 'bed')?.tasks.find((t) => t.id === 'bd-med')?.completed || false,
                mood: !!data.mood,

                // Night Checkup
                nightCheckup: data.taskCategories?.find((c) => c.key === 'bed')?.tasks.find((t) => t.id === 'bd-check')?.completed || false,

                // Other
                snackWater: data.taskCategories?.find((c) => c.key === 'meals')?.tasks.find((t) => t.id === 'o-snack')?.completed || false,

                handoverNote: data.handoverNote || '',
            };
            const response = await apiClient.put<any>(`${SHIFT_HANDOVER_BASE}/${id}`, payload);
            return response.data;
        } catch (error) {
            console.warn('updateHandover API failed, using local fallback', error);
            const index = handovers.findIndex((handover) => handover.id === id);
            if (index > -1) {
                handovers[index] = { ...handovers[index], ...data, updatedAt: new Date().toISOString() };
                return handovers[index];
            }
            throw new Error('Handover not found');
        }
    },

    getHandoverStats: async (facilityId: string): Promise<any> => {
        try {
            const response = await apiClient.get<any>(`${SHIFT_HANDOVER_BASE}/facility/${facilityId}/stats`);
            return response.data;
        } catch (error) {
            console.warn('getHandoverStats API failed', error);
            return null;
        }
    },

    deleteHandover: async (id: string): Promise<void> => {
        try {
            await apiClient.delete(`${SHIFT_HANDOVER_BASE}/${id}`);
        } catch (error) {
            console.warn('deleteHandover API failed, using local fallback', error);
            handovers = handovers.filter((handover) => handover.id !== id);
        }
    },
};

export default careService;
